import { Container, Sprite, Text, type Texture } from 'pixi.js';
import { SkillSlot, type SkillSlotData } from './SkillSlot';

// All layout numbers are fractions of the card's height (h) or width (w) so
// the card scales cleanly from one `height` option. Tune here, render-only.
const TITLE_Y = 0.06;
const TITLE_FONT = 0.034;
const NAME_Y = 0.11;
const NAME_FONT = 0.05;
const AVATAR_CENTER_Y = 0.31;
const AVATAR_SIZE = 0.33;
const LEVEL_Y = 0.5;
const LEVEL_FONT = 0.032;
// Skill grid: 3 columns x 2 rows in the lower half of the card art, fully
// below the painted divider (~0.54h) so icons never overlap it.
const SKILL_COL_X = [0.22, 0.5, 0.78] as const;
const SKILL_ROW_Y = [0.64, 0.835] as const;
const SKILL_ICON_SIZE = 0.145;

const TEXT_COLOR = 0x4a3826;

export type EnemyCardData = {
  title: string; // e.g. "Enemy 1"
  name: string; // e.g. "SparkyBot"
  level: number;
  avatar: Texture;
  // Up to 6 priority skills, laid out 3 per row.
  skills: SkillSlotData[];
};

export type EnemyCardOptions = {
  data: EnemyCardData;
  body: Texture;
  labelBg: Texture;
  // Card height in design px; width follows the body texture aspect.
  height: number;
  fontFamily?: string;
  textResolution: number;
};

export class EnemyCard extends Container {
  readonly cardWidth: number;
  readonly cardHeight: number;

  constructor(opts: EnemyCardOptions) {
    super();
    const { data, body, labelBg, height: h, fontFamily, textResolution } = opts;
    const w = h * (body.width / body.height);
    this.cardWidth = w;
    this.cardHeight = h;

    const bg = new Sprite(body);
    bg.width = w;
    bg.height = h;
    this.addChild(bg);

    const makeText = (text: string, fontSize: number, y: number, bold = false): Text => {
      const t = new Text({
        text,
        style: {
          fontFamily,
          fontSize,
          fill: TEXT_COLOR,
          align: 'center',
          ...(bold ? { fontWeight: 'bold' as const } : {}),
        },
        resolution: textResolution,
      });
      t.anchor.set(0.5);
      t.position.set(w / 2, y);
      this.addChild(t);
      return t;
    };

    makeText(data.title, TITLE_FONT * h, TITLE_Y * h);
    makeText(data.name, NAME_FONT * h, NAME_Y * h);

    const avatar = new Sprite(data.avatar);
    const avatarSize = AVATAR_SIZE * h;
    avatar.anchor.set(0.5);
    avatar.scale.set(avatarSize / Math.max(data.avatar.width, data.avatar.height));
    avatar.position.set(w / 2, AVATAR_CENTER_Y * h);
    this.addChild(avatar);

    makeText(`Level ${data.level}`, LEVEL_FONT * h, LEVEL_Y * h, true);

    data.skills.slice(0, SKILL_COL_X.length * SKILL_ROW_Y.length).forEach((slot, i) => {
      const col = i % SKILL_COL_X.length;
      const row = Math.floor(i / SKILL_COL_X.length);
      const cell = new SkillSlot({
        data: slot,
        labelBg,
        iconSize: SKILL_ICON_SIZE * h,
        fontFamily,
        textResolution,
      });
      cell.position.set(SKILL_COL_X[col]! * w, SKILL_ROW_Y[row]! * h);
      this.addChild(cell);
    });
  }
}
